/**
 * Рождение: год, страна, пол, семья и стартовые состояния.
 *
 * Все случайности — из `Rng`; каждая подсистема берёт свой `fork`, поэтому
 * добавление нового жребия не сдвигает уже существующие. Показатели
 * `u5mr`/`imr` в датасете даны на 100 рождений, здесь переводятся
 * на 1000, как ждут формулы плана и `buildLifeTable`.
 */
import { getCountry, getValue, listCountries, type Dataset } from '@life/data';
import { keysWithPrefix, t } from '@life/i18n';
import { ENERGY_BASE, energyFor } from './energy';
import { generateLook } from './look';
import { createRng, type Rng } from './rng';
import { clampStat, EPOCH_YEARS, type BirthOptions, type DrawMode, type Epoch, type Life, type Sex } from './types';

/** Доля мальчиков среди новорождённых (мировое соотношение полов ~1.05). */
export const MALE_SHARE = 0.512;

const EPOCHS = ['past', 'present', 'future'] as const;

export function drawBirthYear(epoch: Epoch, rng: Rng): number {
  const concrete = epoch === 'any' ? rng.pick(EPOCHS) : epoch;
  const [lo, hi] = EPOCH_YEARS[concrete];
  return rng.int(lo, hi);
}

interface CountryWeights {
  iso3s: string[];
  weights: number[];
}

/** Кэш весов по датасету и ключу `${mode}:${year}` — веса чистые, зависят только от данных. */
const weightsCache = new WeakMap<Dataset, Map<string, CountryWeights>>();

function countryWeights(ds: Dataset, mode: DrawMode, year: number): CountryWeights {
  let byKey = weightsCache.get(ds);
  if (!byKey) {
    byKey = new Map();
    weightsCache.set(ds, byKey);
  }
  const key = `${mode}:${year}`;
  const hit = byKey.get(key);
  if (hit) return hit;

  const countries = listCountries(ds);
  const iso3s: string[] = new Array(countries.length);
  const weights: number[] = new Array(countries.length);
  for (let i = 0; i < countries.length; i += 1) {
    const c = countries[i]!;
    iso3s[i] = c.iso3;
    weights[i] = Math.max(0, getValue(ds, c.iso3, mode, year).v);
  }
  const built = { iso3s, weights };
  byKey.set(key, built);
  return built;
}

/** Страна по весам `births` или `population` за ближайший к `year` год. */
export function drawCountry(ds: Dataset, mode: DrawMode, year: number, rng: Rng): string {
  const { iso3s, weights } = countryWeights(ds, mode, year);
  return rng.weighted(iso3s, weights);
}

function drawName(sex: Sex, rng: Rng): string {
  return t(rng.pick(keysWithPrefix(`name.${sex}.`)));
}

export function createLife(ds: Dataset, opts: BirthOptions): Life {
  const rng = createRng(opts.seed);

  const birthYear = drawBirthYear(opts.epoch, rng.fork('birthYear'));
  const iso3 = opts.iso3 ?? drawCountry(ds, opts.drawMode, birthYear, rng.fork('country'));
  const country = getCountry(ds, iso3);

  const sex: Sex = rng.fork('sex').chance(MALE_SHARE) ? 'm' : 'f';
  const familyDecile = rng.fork('family').int(1, 10);

  const urbanShare = getValue(ds, iso3, 'urban', birthYear).v;
  const urban = rng.fork('urban').chance(urbanShare / 100);

  const fertility = getValue(ds, iso3, 'fertility', birthYear).v;
  const siblings = Math.round(Math.max(0, rng.fork('siblings').normal(fertility - 1, 1)));

  const u5mrPer1000 = getValue(ds, iso3, 'u5mr', birthYear).v * 10;
  const schoolYears = getValue(ds, iso3, 'schoolYears', birthYear).v;
  const health = 70 - 30 * Math.min(1, u5mrPer1000 / 200) + rng.fork('health').normal(0, 5);
  const knowledge = 10 + 20 * Math.min(1, schoolYears / 14) + (familyDecile - 5.5);

  const name = opts.name ?? drawName(sex, rng.fork('name'));

  const life: Life = {
    id: opts.seed,
    seed: opts.seed,
    name,
    sex,
    iso3,
    birthYear,
    age: 0,
    alive: true,
    stats: {
      health: clampStat(health),
      happiness: 60,
      knowledge: clampStat(knowledge),
      relations: 60,
    },
    money: 0,
    debt: 0,
    job: null,
    education: 'none',
    inSchool: false,
    schoolYearsDone: 0,
    urban,
    familyDecile,
    siblings,
    partner: false,
    children: 0,
    habits: { smoking: false, alcohol: false, gambling: false },
    conditions: [],
    flags: {},
    focus: 'play',
    log: [{ age: 0, key: 'birth.born', params: { name, country: country.nameRu, year: birthYear } }],
    death: null,
    pendingDecision: null,
    look: generateLook(sex, country.whoRegion, rng.fork('look')),
    assets: { housing: 'family', car: false },
    parentsAlive: 2,
    childrenAges: [],
    energy: ENERGY_BASE,
    visits: {},
    dream: null,
    pendingDream: null,
    yearGoal: null,
    milestones: [],
  };
  return { ...life, energy: energyFor(life) };
}
